import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Star, MapPin, CheckCircle, Shield, BadgeCheck, Clock, Briefcase,
  ChevronLeft, Camera, MessageSquare, FileText, Loader2, Award, Calendar
} from 'lucide-react';
import { serviceMarketplaceAPI, providerReviewsAPI } from '../services/api';
import ProviderReviewsList from '../components/ProviderReviewsList';

const BADGE_CONFIG = {
  verified: { icon: CheckCircle, label: 'Documents verified', color: 'text-green-700 bg-green-100 border-green-200' },
  insured: { icon: Shield, label: 'Insurance on file', color: 'text-blue-700 bg-blue-100 border-blue-200' },
  dbs_checked: { icon: BadgeCheck, label: 'DBS on file', color: 'text-purple-700 bg-purple-100 border-purple-200' },
  right_to_work: { icon: BadgeCheck, label: 'Right to work', color: 'text-teal-700 bg-teal-100 border-teal-200' },
};

const PublicProviderProfilePage = () => {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about');
  const [reviewData, setReviewData] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [profile, reviews] = await Promise.all([
          serviceMarketplaceAPI.getPublicProfile(providerId),
          providerReviewsAPI.getForProvider(providerId)
        ]);
        setProvider(profile);
        setReviewData(reviews);
      } catch (err) {
        console.error('Failed to load provider:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [providerId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#1F3A68]" size={40} />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-4">
        <h2 className="text-xl font-bold text-gray-700">Provider not found</h2>
        <button onClick={() => navigate('/services')} className="text-[#1F3A68] hover:underline">
          ← Back to Marketplace
        </button>
      </div>
    );
  }

  const badges = provider.verificationBadges || [];
  const rating = provider.averageRating || 0;
  const reviews = provider.totalReviews || 0;
  const hourlyRate = provider.hourlyRate;

  const tabs = [
    { id: 'about', label: 'About' },
    { id: 'services', label: 'Services & Pricing' },
    { id: 'portfolio', label: 'Portfolio' },
    { id: 'reviews', label: `Reviews (${reviews})` },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Back Button */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
            <ChevronLeft size={18} /> Back
          </button>
        </div>
      </div>

      {/* Cover Photo */}
      <div className="h-48 sm:h-64 bg-gradient-to-br from-[#1F3A68] to-[#2d5499] relative">
        {provider.coverPhoto && (
          <img src={provider.coverPhoto} alt="" className="w-full h-full object-cover" />
        )}
      </div>

      {/* Profile Header */}
      <div className="max-w-5xl mx-auto px-4 -mt-16 relative z-[1]">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {provider.image ? (
                <img src={provider.image} alt={provider.businessName} className="w-24 h-24 rounded-xl border-4 border-white shadow object-cover" />
              ) : (
                <div className="w-24 h-24 rounded-xl bg-[#1F3A68] text-white flex items-center justify-center text-3xl font-bold border-4 border-white shadow">
                  {(provider.businessName || provider.name || 'P')[0]}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-gray-900">{provider.businessName || provider.name}</h1>
              {provider.tagline && <p className="text-gray-500 mt-0.5">{provider.tagline}</p>}

              <div className="flex flex-wrap items-center gap-3 mt-2">
                {/* Stars */}
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star key={s} size={16} className={s <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} />
                  ))}
                  <span className="ml-1 font-semibold text-gray-700">{rating > 0 ? rating.toFixed(1) : '—'}</span>
                  <span className="text-sm text-gray-400">({reviews} review{reviews !== 1 ? 's' : ''})</span>
                </div>

                {provider.yearsExperience > 0 && (
                  <span className="flex items-center gap-1 text-sm text-gray-500">
                    <Briefcase size={14} /> {provider.yearsExperience} year{provider.yearsExperience !== 1 ? 's' : ''} experience
                  </span>
                )}

                {provider.completedTasks > 0 && (
                  <span className="flex items-center gap-1 text-sm text-gray-500">
                    <Award size={14} /> {provider.completedTasks} job{provider.completedTasks !== 1 ? 's' : ''} completed
                  </span>
                )}
              </div>

              {/* Badges */}
              {badges.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {badges.map(badge => {
                    const config = BADGE_CONFIG[badge];
                    if (!config) return null;
                    const Icon = config.icon;
                    return (
                      <span key={badge} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}>
                        <Icon size={14} /> {config.label}
                      </span>
                    );
                  })}
                </div>
              )}
              {badges.length > 0 && (
                <p className="text-[11px] text-gray-400 mt-1.5">
                  Badges show documents this provider has supplied to Ariv, not a guarantee by Ariv.
                </p>
              )}
            </div>

            {/* CTA */}
            <div className="flex flex-col gap-2 sm:items-end">
              {hourlyRate && (
                <div className="text-right">
                  <span className="text-2xl font-bold text-[#1F3A68]">£{hourlyRate}</span>
                  <span className="text-sm text-gray-400">/hr</span>
                </div>
              )}
              <button
                onClick={() => navigate(`/services/request?provider=${providerId}&category=${(provider.serviceTypes || [])[0] || ''}`)}
                className="px-6 py-2.5 bg-[#1F3A68] text-white font-semibold rounded-lg hover:bg-[#162d52] transition-colors flex items-center gap-2"
              >
                <FileText size={16} /> Request Quote
              </button>
              {provider.serviceArea?.postcodes?.length > 0 && (
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  <MapPin size={12} />
                  Serves: {provider.serviceArea.postcodes.slice(0, 4).join(', ')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-5xl mx-auto px-4 mt-6">
        <div className="flex gap-1 border-b">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-[#1F3A68] text-[#1F3A68]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-5xl mx-auto px-4 py-6 pb-16">
        {activeTab === 'about' && (
          <div className="space-y-6">
            {/* Bio */}
            {provider.bio && (
              <div className="bg-white rounded-xl border p-6">
                <h3 className="font-bold text-gray-900 mb-3">About</h3>
                <p className="text-gray-600 whitespace-pre-line">{provider.bio}</p>
              </div>
            )}

            {/* Qualifications */}
            {provider.qualifications?.length > 0 && (
              <div className="bg-white rounded-xl border p-6">
                <h3 className="font-bold text-gray-900 mb-3">Qualifications</h3>
                <div className="space-y-2">
                  {provider.qualifications.map((q, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                      <span className="text-gray-600">{q}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Service Area */}
            {provider.serviceArea?.postcodes?.length > 0 && (
              <div className="bg-white rounded-xl border p-6">
                <h3 className="font-bold text-gray-900 mb-3">Service Area</h3>
                <div className="flex flex-wrap gap-2">
                  {provider.serviceArea.postcodes.map((pc, i) => (
                    <span key={i} className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                      {pc}
                    </span>
                  ))}
                </div>
                {provider.serviceArea.radiusMiles && (
                  <p className="text-sm text-gray-400 mt-2">Within {provider.serviceArea.radiusMiles} miles</p>
                )}
              </div>
            )}

            {/* Member Info */}
            <div className="bg-white rounded-xl border p-6">
              <h3 className="font-bold text-gray-900 mb-3">Provider Info</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                {provider.memberSince && (
                  <div>
                    <span className="text-gray-400 block">Member since</span>
                    <span className="text-gray-700 font-medium">{new Date(provider.memberSince).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</span>
                  </div>
                )}
                <div>
                  <span className="text-gray-400 block">Jobs completed</span>
                  <span className="text-gray-700 font-medium">{provider.completedTasks || 0}</span>
                </div>
                <div>
                  <span className="text-gray-400 block">Rating</span>
                  <span className="text-gray-700 font-medium">{rating > 0 ? `${rating.toFixed(1)} / 5` : 'No ratings yet'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'services' && (
          <div className="space-y-6">
            {/* Service Types */}
            <div className="bg-white rounded-xl border p-6">
              <h3 className="font-bold text-gray-900 mb-4">Services Offered</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(provider.serviceTypes || []).map(type => (
                  <div key={type} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 rounded-lg bg-[#1F3A68]/10 text-[#1F3A68] flex items-center justify-center">
                      <Briefcase size={16} />
                    </div>
                    <span className="font-medium text-gray-700 capitalize">{type.replace(/_/g, ' ')}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Subcategories */}
            {provider.serviceSubcategories?.length > 0 && (
              <div className="bg-white rounded-xl border p-6">
                <h3 className="font-bold text-gray-900 mb-3">Specializations</h3>
                <div className="flex flex-wrap gap-2">
                  {provider.serviceSubcategories.map(sub => (
                    <span key={sub} className="px-3 py-1.5 bg-[#1F3A68]/5 text-[#1F3A68] rounded-full text-sm font-medium capitalize">
                      {sub.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Pricing */}
            <div className="bg-white rounded-xl border p-6">
              <h3 className="font-bold text-gray-900 mb-4">Pricing</h3>
              {provider.pricing?.customQuotesOnly ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-800 font-medium">Custom Quotes Only</p>
                  <p className="text-amber-600 text-sm mt-1">This provider offers bespoke pricing based on job requirements. Request a quote for an accurate price.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {hourlyRate && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700">Hourly Rate</span>
                      <span className="text-lg font-bold text-[#1F3A68]">£{hourlyRate}/hr</span>
                    </div>
                  )}
                  {provider.pricing?.perJobRates && Object.entries(provider.pricing.perJobRates).map(([job, rate]) => (
                    <div key={job} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-700 capitalize">{job.replace(/_/g, ' ')}</span>
                      <span className="font-bold text-[#1F3A68]">£{rate}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'portfolio' && (
          <div className="bg-white rounded-xl border p-6">
            <h3 className="font-bold text-gray-900 mb-4">Portfolio</h3>
            {(provider.portfolioPhotos || []).length === 0 ? (
              <div className="text-center py-12">
                <Camera size={40} className="mx-auto mb-3 text-gray-300" />
                <p className="text-gray-400">No portfolio photos yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {provider.portfolioPhotos.map((photo, i) => (
                  <div key={i} className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                    <img src={photo} alt={`Portfolio ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'reviews' && (
          <ProviderReviewsList
            providerId={providerId}
            reviewData={reviewData}
          />
        )}
      </div>
    </div>
  );
};

export default PublicProviderProfilePage;
